import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Send,
  Bot,
  User,
  Sparkles,
  Code,
  BookOpen,
  Lightbulb,
  Copy,
  Check,
  ChevronRight,
} from "lucide-react";
import "../../../../assets/css/student/AITutorLayout/ChatInterface/ChatInterface.css";

/* ─── Statics ─────────────────────────────────────────────────── */

const PROMPTS = [
  {
    icon: Code,
    text: "Explain async/await in JavaScript",
    meta: "JavaScript · Intermediate",
  },
  {
    icon: BookOpen,
    text: "What are React Hooks and when to use them?",
    meta: "React · Beginner",
  },
  {
    icon: Lightbulb,
    text: "Difference between SQL and NoSQL databases",
    meta: "Databases · Concept",
  },
];

const TUTOR_RESPONSES = [
  "Here's the core concept — no fluff, just facts:\n\nThis connects to your AI backend in production.\n\n```javascript\nconst fetchData = async (url) => {\n  const res = await fetch(url);\n  if (!res.ok) throw new Error('Request failed');\n  return res.json();\n};\n```\n\nCommon mistakes: missing error handling, overusing async unnecessarily. Avoid both.",
  "Direct answer — the distinction matters more than you think.\n\nUnderstand the *why* before the *how*. Most developers skip this step entirely.\n\nWant me to go deeper on any specific part?",
  "The concept is simpler than most tutorials make it appear.\n\n```python\n# Core pattern\ndef filter_positive(data):\n    return [x for x in data if x > 0]\n```\n\nIf you cannot explain it to someone else, you haven't fully understood it yet.",
];

const BUDDY_RESPONSES = [
  "Great question! Let's break this down together 😊\n\nHere's a friendly example:\n\n```javascript\n// Simpler than it looks!\nconst greet = async (name) => {\n  const msg = await generateGreeting(name);\n  return msg;\n};\n```\n\nEveryone starts somewhere — you're asking the right questions!",
  "Oh, I love this one! It clicks really nicely once you see it.\n\nThink of it like ordering food — you don't stare at the door the whole time. You wait for a notification. That's basically what's happening here! 🍕",
  "You've totally got this! Let's go step by step, no rush at all.\n\nFocus on one piece at a time. Want me to slow down on any part? That's completely okay 💪",
];

const INITIAL_MSG = (mode) => ({
  id: 1,
  type: "bot",
  mode,
  text:
    mode === "tutor"
      ? "I'm Techify AI in Tutor mode. I'll be direct, honest, and focused on building real understanding — no sugarcoating.\n\nWhat are we working on?"
      : "Hey! I'm Techify AI in Buddy mode 😊 I'm here to make learning fun and stress-free.\n\nWhat would you like to explore today?",
  timestamp: new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  }),
});

/* ─── CodeBlock ───────────────────────────────────────────────── */

const detectLang = (code) => {
  if (/import React|useState|useEffect|jsx/.test(code)) return "jsx";
  if (/def |print\(|import /.test(code)) return "python";
  if (/SELECT|FROM|WHERE/i.test(code)) return "sql";
  if (/<html|<div|<span/.test(code)) return "html";
  return "javascript";
};

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const firstLine = code.split("\n")[0].trim();
  const hasLangTag = firstLine && /^[a-z]+$/.test(firstLine);
  const lang = hasLangTag ? firstLine : detectLang(code);
  const cleanCode = hasLangTag
    ? code.replace(/^[^\n]*\n/, "").trim()
    : code.trim();

  const handleCopy = () => {
    navigator.clipboard.writeText(cleanCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="tcp-code">
      <div className="tcp-code__bar">
        <span className="tcp-code__lang">{lang}</span>
        <button
          className="tcp-code__copy"
          onClick={handleCopy}
          aria-label="Copy code"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          <span>{copied ? "Copied!" : "Copy"}</span>
        </button>
      </div>
      <pre className="tcp-code__pre">
        <code>{cleanCode}</code>
      </pre>
    </div>
  );
};

/* ─── MessageText ─────────────────────────────────────────────── */

const MessageText = ({ text }) => {
  const segs = text.split(/(```[\s\S]*?```)/g);
  return (
    <>
      {segs.map((seg, i) => {
        if (seg.startsWith("```") && seg.endsWith("```")) {
          return <CodeBlock key={i} code={seg.slice(3, -3)} />;
        }
        return seg.split("\n").map((line, j) =>
          line.trim() ? (
            <p key={`${i}-${j}`} className="tcp-msg__para">
              {line}
            </p>
          ) : null,
        );
      })}
    </>
  );
};

/* ─── AIChatPage ──────────────────────────────────────────────── */

const AIChatPage = ({ mode = "tutor" }) => {
  const [messages, setMessages] = useState([INITIAL_MSG(mode)]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [responseIndex, setResponseIndex] = useState(0);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 160) + "px";
    }
  }, [inputMessage]);

  const ts = () =>
    new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const handleSend = useCallback(() => {
    const text = inputMessage.trim();
    if (!text || isTyping) return;

    setMessages((prev) => [
      ...prev,
      { id: Date.now(), type: "user", text, timestamp: ts() },
    ]);
    setInputMessage("");
    setIsTyping(true);

    setTimeout(() => {
      const pool = mode === "tutor" ? TUTOR_RESPONSES : BUDDY_RESPONSES;
      const reply = pool[responseIndex % pool.length];
      setResponseIndex((i) => i + 1);
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, type: "bot", mode, text: reply, timestamp: ts() },
      ]);
      setIsTyping(false);
    }, 1700);
  }, [inputMessage, isTyping, mode, responseIndex]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePromptClick = (text) => {
    setInputMessage(text);
    textareaRef.current?.focus();
  };

  const handleAction = (action) => {
    const text =
      action === "explain"
        ? "Can you explain that in more detail?"
        : "Can you simplify that explanation?";
    setInputMessage(text);
    textareaRef.current?.focus();
  };

  return (
    <div className={`tcp-root tcp--${mode}`}>
      {/* ── Thread ── */}
      <section
        className="tcp-thread"
        aria-label="Conversation"
        aria-live="polite"
      >
        {messages.length === 1 && (
          <div className="tcp-welcome">
            <div className="tcp-welcome__badge">
              <Sparkles size={22} />
            </div>
            <h1 className="tcp-welcome__heading">
              {mode === "tutor"
                ? "What do you want to master?"
                : "Hey! What are we learning today? 😊"}
            </h1>
            <p className="tcp-welcome__sub">
              {mode === "tutor"
                ? "Ask anything. I'll give you direct, honest answers that build real skills."
                : "No pressure, no rush! I'll explain everything in the friendliest way."}
            </p>
            <div className="tcp-prompts">
              {PROMPTS.map(({ icon: Icon, text, meta }) => (
                <button
                  key={text}
                  className="tcp-prompt"
                  onClick={() => handlePromptClick(text)}
                >
                  <span className="tcp-prompt__icon">
                    <Icon size={14} />
                  </span>
                  <span className="tcp-prompt__body">
                    <span className="tcp-prompt__text">{text}</span>
                    <span className="tcp-prompt__meta">{meta}</span>
                  </span>
                  <ChevronRight
                    size={13}
                    className="tcp-prompt__arrow"
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <article
            key={msg.id}
            className={`tcp-msg ${
              msg.type === "user"
                ? "tcp-msg--user"
                : `tcp-msg--bot tcp-msg--${msg.mode || mode}`
            }`}
          >
            {msg.type === "bot" && (
              <div className="tcp-avatar tcp-avatar--bot" aria-hidden="true">
                <Bot size={12} />
              </div>
            )}

            <div className="tcp-msg__wrap">
              {msg.type === "bot" && (
                <span className="tcp-msg__label">
                  Techify AI · {msg.mode === "tutor" ? "Tutor" : "Buddy"}
                </span>
              )}
              <div className="tcp-msg__bubble">
                <MessageText text={msg.text} />
              </div>
              {msg.type === "bot" && (
                <div className="tcp-msg__actions">
                  <button
                    className="tcp-msg__action"
                    onClick={() => handleAction("explain")}
                  >
                    Explain more
                  </button>
                  <button
                    className="tcp-msg__action"
                    onClick={() => handleAction("simplify")}
                  >
                    Simplify
                  </button>
                </div>
              )}
              <time className="tcp-msg__time">{msg.timestamp}</time>
            </div>

            {msg.type === "user" && (
              <div className="tcp-avatar tcp-avatar--user" aria-hidden="true">
                <User size={12} />
              </div>
            )}
          </article>
        ))}

        {isTyping && (
          <article className="tcp-msg tcp-msg--bot tcp-msg--typing">
            <div className="tcp-avatar tcp-avatar--bot" aria-hidden="true">
              <Bot size={12} />
            </div>
            <div className="tcp-msg__wrap">
              <span className="tcp-msg__label">
                Techify AI · {mode === "tutor" ? "Tutor" : "Buddy"}
              </span>
              <div className="tcp-msg__bubble">
                <div
                  className="tcp-thinking"
                  aria-label="Techify AI is thinking..."
                >
                  <div className="tcp-thinking__dots">
                    <span />
                    <span />
                    <span />
                  </div>
                  <span className="tcp-thinking__text">
                    Analyzing your question…
                  </span>
                </div>
              </div>
            </div>
          </article>
        )}

        <div ref={messagesEndRef} aria-hidden="true" style={{ height: 16 }} />
      </section>

      {/* ── Composer ── */}
      <div className={`tcp-composer ${inputFocused ? "is-focused" : ""}`}>
        <div className="tcp-composer__field">
          <textarea
            ref={textareaRef}
            className="tcp-composer__input"
            placeholder={
              mode === "tutor"
                ? "Ask a question or paste code…"
                : "Ask me anything, I'm here to help! 😊"
            }
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            rows={1}
            autoComplete="off"
            aria-label="Message input"
          />
          <button
            className="tcp-composer__send"
            onClick={handleSend}
            disabled={!inputMessage.trim() || isTyping}
            aria-label="Send"
          >
            {isTyping ? (
              <span className="tcp-spinner" aria-hidden="true" />
            ) : (
              <Send size={13} />
            )}
          </button>
        </div>
        <p className="tcp-composer__hint">
          <kbd>Enter</kbd> to send &nbsp;·&nbsp; <kbd>Shift+Enter</kbd> for new
          line
        </p>
      </div>
    </div>
  );
};

export default AIChatPage;
